import { useSWR } from "./useSWR.ts";
import { localDateStr, shiftDate, toMinutes, formatTime, todayStr } from "./utils.ts";
import { classifyClass } from "./classCategory.ts";
import type { ClassEvent } from "./classCategory.ts";

const WEEKDAY_NAMES = ["ma", "ti", "ke", "to", "pe", "la", "su"];

function getMondayOfWeek(dateStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return localDateStr(d);
}

async function fetchWeek(key: string): Promise<Record<string, ClassEvent[]>> {
  const monday = key.replace("week/", "");
  const sunday = shiftDate(monday, 6);
  const r = await fetch(`/api/classes/range?start=${monday}&end=${sunday}`);
  if (r.status === 401) throw new Error("__401__");
  const data = await r.json();
  if (data?.error) throw new Error(data.error);
  return data;
}

export function WeekView({
  date,
  onSelect,
  onDaySelect,
  onSessionExpired,
  hiddenGyms,
  selectedClassId,
}: {
  date: string;
  onSelect: (id: string) => void;
  onDaySelect: (date: string) => void;
  onSessionExpired: () => void;
  hiddenGyms: string[];
  selectedClassId?: string | null;
}) {
  const monday = getMondayOfWeek(date);
  const { data: weekData, error, isLoading } = useSWR(
    `week/${monday}`,
    fetchWeek,
    { refreshInterval: 60_000, initialDelay: 10_000 },
  );

  if (error === "__401__") { onSessionExpired(); return null; }
  if (error) return <div className="loading">{error}</div>;
  if (isLoading) return <div className="loading"><div className="spinner" /></div>;

  const today = todayStr();
  const days: string[] = [];
  for (let i = 0; i < 7; i++) {
    days.push(shiftDate(monday, i));
  }

  return (
    <div className="week-view">
      {days.map((day, i) => {
        const isToday = day === today;
        const dayDate = new Date(day + "T12:00:00");
        const dayNum = dayDate.getDate();
        const events = (weekData?.[day] || [])
          .filter((cls: ClassEvent) => {
            const gym = cls.employee_names?.[0]?.service_provider_name || "Muu";
            return !hiddenGyms.includes(gym);
          })
          .sort((a: ClassEvent, b: ClassEvent) => a.start.localeCompare(b.start));

        return (
          <div key={day} className={`week-day${isToday ? " week-day-today" : ""}`}>
            <div className="week-day-header" onClick={() => onDaySelect(day)}>
              <span className="week-day-name">{WEEKDAY_NAMES[i]}</span>
              <span className="week-day-num">{dayNum}</span>
            </div>
            <div className="week-day-classes">
              {events.map((cls: ClassEvent) => {
                const count = cls.customer?.length || 0;
                const max = cls.maxparticipants || 0;
                return (
                  <div
                    key={cls.id}
                    className={`week-class-chip${cls.id === selectedClassId ? " week-chip-selected" : ""}`}
                    style={{ borderLeftColor: `var(--cat-${classifyClass(cls.productname)})` }}
                    onClick={() => onSelect(cls.id)}
                  >
                    <span className="chip-time">{formatTime(cls.start)}</span>
                    <span className="chip-name">{cls.productname?.trim()}</span>
                    <span className="chip-count">{count}/{max || "?"}</span>
                  </div>
                );
              })}
              {events.length === 0 && (
                <div className="week-day-empty" onClick={() => onDaySelect(day)}>-</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
